import { useToken } from "@frend-digital/centra/client";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useMemo, useRef } from "react";
import { createFilters } from "./utils";
import type { DepictOptions } from "@frend-digital/depict/server";
import { fetchProductsClientAction, type ProductListData } from "./actions";

interface UseListingOptions {
	serverOptions: DepictOptions;
	filterKeys: string[];
	searchParams?: URLSearchParams;
	token?: string;
}

/**
 * Isomorphic plp options that can be used both on server and client
 * @returns {DepictOptions}
 */
export const useListingOptions = ({
	serverOptions,
	filterKeys,
	searchParams,
	token,
}: {
	serverOptions: DepictOptions;
	filterKeys: string[];
	searchParams?: URLSearchParams;
	token?: string | null;
}) => {
	const clientOptions = useMemo(() => {
		const newParams = new URLSearchParams(searchParams);

		const inStock = newParams.get("in_stock");
		if (inStock === "false") {
			newParams.delete("in_stock");
		} else {
			newParams.set("in_stock", "true");
		}

		const entriesToObject = Array.from(newParams?.entries() || []).reduce(
			(acc, [key, value]) => {
				if (acc[key]) {
					if (Array.isArray(acc[key])) {
						acc[key] = [...acc[key], value];
					} else {
						acc[key] = [acc[key] as string, value];
					}
				} else {
					acc[key] = value;
				}

				return acc;
			},
			{} as Record<string, string | string[]>,
		);

		const { filterValues, sort, cache } = createFilters(entriesToObject, {
			filterKeys,
		});

		const query = searchParams?.get("q") || undefined;

		return [
			{
				...serverOptions,
				query: query,
				sort,
				filters: filterValues,
				market: serverOptions.market,
				session_id: token,
			} as unknown as DepictOptions,
			cache && !query,
		] as const;
	}, [serverOptions, searchParams, filterKeys, token]);

	return clientOptions;
};

export const useProductListingQuery = ({
	initialData,
	depictCategory,
	...args
}: UseListingOptions & {
	initialData?: ProductListData;
	depictCategory?: string;
}) => {
	const token = useToken();
	const [clientOptions, cache] = useListingOptions({ ...args, token });

	/**
	 * Keeping track of when the data was last updated to keep previous data
	 */

	const trackedInitialData = useRef<ProductListData | undefined>(initialData as ProductListData);

	return useInfiniteQuery({
		queryKey: ["fetchProducts", clientOptions, depictCategory],
		queryFn: async ({ pageParam: cursor }) => {
			const data = await fetchProductsClientAction({
				cursor,
				depictOptions: clientOptions,
				cache: cache,
				depictCategory,
			});

			trackedInitialData.current = data;

			return data;
		},
		initialData:
			!!trackedInitialData.current && !clientOptions.query
				? {
						pageParams: [undefined],
						pages: [trackedInitialData.current],
					}
				: undefined,
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (lastPage) => lastPage?.nextCursor ?? undefined,

		placeholderData: keepPreviousData,

		refetchOnWindowFocus: false,
		enabled: !!token,
	});
};
